import { BonusType, clone } from "@pentacode/core";
import { css, html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import { alert, confirm } from "../../elements/alert-dialog";
import "../../elements/scroller";
import "../../elements/sortable-list";
import { app } from "../../init";
import { singleton } from "../../lib/singleton";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { shared } from "../../styles";
import "./components/bonus-type-dialog";
import type { BonusTypeDialog } from "./components/bonus-type-dialog";
import "./components/bonus-type-item";

@customElement("ptc-settings-bonuses")
export class SettingsBonuses extends Routing(StateMixin(LitElement)) {
    routePattern = /settings\/bonuses/;

    @state()
    private _loading = false;

    @state()
    private _bonusTypes: BonusType[] = [];

    @singleton("ptc-bonus-type-dialog")
    private _bonusTypeDialog: BonusTypeDialog;

    private _reset() {
        this._bonusTypes = app.company?.bonusTypes?.map(clone) || [];
    }

    updated(changes: Map<string, unknown>) {
        if (changes.has("active") && this.active) {
            this._reset();
        }
    }

    private async _addBonusType(bonusType = new BonusType()) {
        const submitted = await this._bonusTypeDialog.show(bonusType);
        if (submitted) {
            this._bonusTypes.push(bonusType);
            await this._submit();
        }
    }

    private async _editBonusType(type: BonusType) {
        const changed = await this._bonusTypeDialog.show(type);
        if (changed) {
            await this._submit();
        }
    }

    private async _removeBonusType(index: number) {
        if (
            await confirm("Sind Sie sicher dass Sie diesen Zuschlagsart löschen möchten?", "Löschen", "Abbrechen", {
                type: "destructive",
            })
        ) {
            this._bonusTypes.splice(index, 1);
            await this._submit();
        }
    }

    private async _duplicateBonusType(type: BonusType) {
        const duplicate = clone(type);
        // @ts-expect-error - intentionally deleting required property for duplication
        delete duplicate.id;
        duplicate.name += " (Kopie)";
        // @ts-expect-error - intentionally deleting required property for duplication
        duplicate.overrides.forEach((o) => delete o.id);
        await this._addBonusType(duplicate);
    }

    private async _orderChanged() {
        this._bonusTypes.forEach((t, i) => (t.order = i));
        await this._submit();
    }

    private async _submit() {
        if (!app.company) {
            return;
        }
        this._loading = true;
        try {
            await app.updateCompany({ bonusTypes: this._bonusTypes });
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }
        this._reset();
        this._loading = false;
    }

    static styles = [shared, css``];

    render() {
        return html`
            <ptc-scroller class="fullbleed">
                <div class="double-padded narrow-content">
                    <div class="padded">
                        <h2>Zuschlagsarten</h2>
                        <div class="box">
                            <ptc-sortable-list
                                .items=${this._bonusTypes}
                                .renderItem=${(type: BonusType, i: number) => html`
                                    <div class="border-bottom relative">
                                        <ptc-bonus-type-item
                                            .bonusType=${type}
                                            @click=${() => this._editBonusType(type)}
                                            class="click"
                                            style="padding-right: 3em;"
                                        ></ptc-bonus-type-item>

                                        <button
                                            id="bonusTypeMenuButton${i}"
                                            class="transparent slim margined top right absolute"
                                            type="button"
                                        >
                                            <ptc-ui-icon name="icon.action.show-more"></ptc-ui-icon>
                                        </button>

                                        <ptc-ui-popover
                                            for="bonusTypeMenuButton${i}"
                                            placement="bottom"
                                            class="popover-menu"
                                            data-popover="close"
                                        >
                                            <button @click=${() => this._editBonusType(type)} type="button">
                                                <ptc-ui-icon name="icon.action.edit"></ptc-ui-icon>
                                                Bearbeiten
                                            </button>

                                            <button @click=${() => this._removeBonusType(i)} type="button">
                                                <ptc-ui-icon name="icon.action.delete"></ptc-ui-icon>
                                                Entfernen
                                            </button>

                                            <button @click=${() => this._duplicateBonusType(type)} type="button">
                                                <ptc-ui-icon name="icon.action.duplicate"></ptc-ui-icon>
                                                Duplizieren
                                            </button>
                                        </ptc-ui-popover>
                                    </div>
                                `}
                                @item-moved=${this._orderChanged}
                            ></ptc-sortable-list>

                            <div class="margined vertical layout" @click=${() => this._addBonusType()}>
                                <button class="transparent">
                                    <ptc-ui-icon name="icon.action.add"></ptc-ui-icon> Neue Zuschlagsart
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </ptc-scroller>

            <div class="fullbleed center-aligning center-justifying vertical layout scrim" ?hidden=${!this._loading}>
                <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
            </div>
        `;
    }
}
